import { randomId, useCounter } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { DeviceGetResponse } from 'api/actions/device-get/device-get-response';
import { useApi } from 'api/api-context';
import { DeviceEvidenceFieldType } from 'components/forms/device/DeviceEvidenceField';
import DeviceForm, { DeviceFormData } from 'components/forms/device/DeviceForm';
import { getDeviceFormReferrer } from 'components/forms/device/device-form-referrer';
import { DeviceFileType, getDeviceFileTypeAccessPermission } from 'model/DeviceFileType';
import { stringifyDate } from 'components/inputs/DateInput';
import { addMonths } from 'date-fns';
import {
  DEVICE_REDIRECT_AFTER_SAVE,
  FEATURE_TOGGLE_DEVICE_QR_CODES,
  FEATURE_TOGGLE_MOVE_DEVICE_BETWEEN_DEPARTMENTS,
} from 'env';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { DEVICES_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Page used to edit a device.
 *
 */
export default function EditDevicePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceId } = useParams();
  const [refreshToken, { increment: forceRefresh }] = useCounter(0);
  const { getAction } = useApi();
  const [device, setDevice] = useState<DeviceGetResponse | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const deviceFormReferrer = useMemo(() => getDeviceFormReferrer(location.state), [location.state]);

  useEffect(() => {
    if (deviceId) {
      const deviceGet = getAction('DeviceGet');
      deviceGet({ parameters: { deviceId } }).then(setDevice).catch(panic);
    }
  }, [deviceId, refreshToken]);

  useEffect(() => {
    if (device) {
      const hasPermission = getAction('AuthUserHasPermissionInDepartment');
      hasPermission({
        parameters: { departmentId: String(device.department.departmentId), permissionSlug: 'manage-devices' },
      })
        .then(({ hasPermission }) => {
          setCanEdit(hasPermission);
          setLoading(false);
        })
        .catch(panic);
    }
  }, [device]);

  if (!deviceId) {
    return <Navigate to={DEVICES_PAGE_PATH.original} />;
  }

  /**
   * Handles the form submission.
   */
  function onSubmit({
    deviceName,
    manufacturer,
    serialNumber,
    qrId,
    manufactured,
    internalPossessionsNumber,
    internalNote,
    building,
    floor,
    room,
    longitude,
    latitude,
    deviceTypeId,
    deviceSubtypeId,
    departmentId,
    responsiblePersonId,
    responsiblePersonNote,
    externalId,
    nextRevision,
    files,
    faults,
    evidenceFields,
    structure = 'simple',
    parentDeviceId,
    childDeviceIds,
  }: DeviceFormData) {
    const deviceUpdateAction = getAction('DeviceUpdate');
    const hasPermissionInDepartment = getAction('AuthUserHasPermissionInDepartment');
    const targetDepartmentId = Number(departmentId);
    const qrPayload = FEATURE_TOGGLE_DEVICE_QR_CODES && qrId !== '' ? { qrId } : {};

    const fileTypes = [...new Set(files.map((file) => file.type))] as DeviceFileType[];

    return Promise.all(
      fileTypes.map(async (fileType) => {
        const { hasPermission } = await hasPermissionInDepartment({
          parameters: {
            departmentId: String(targetDepartmentId),
            permissionSlug: getDeviceFileTypeAccessPermission(fileType),
          },
        });

        return [fileType, hasPermission] as const;
      })
    )
      .then((permissionEntries) => {
        const fileTypePermissions = permissionEntries.reduce((acc, [fileType, hasPermission]) => {
          acc[fileType] = hasPermission;
          return acc;
        }, {} as Partial<Record<DeviceFileType, boolean>>);

        /** Checks whether the current user can access the given device file type in target department. */
        const hasFileTypeAccess = (fileType: DeviceFileType) => fileTypePermissions[fileType] === true;

        const mappedFiles = files
          .filter((val) => hasFileTypeAccess(val.type))
          .map((val) => ({
            deviceFileId: val.deviceFileId ?? undefined,
            fileName: val.fileName,
            fileId: val.fileId,
            fileType: val.type,
            deleted: val.deleted,
            isMainImage: val.isMainImage,
          }));

        const mappedRevisions = nextRevision
          .filter(({ description, revisionPeriod }) => Number(revisionPeriod) !== 0 && description !== '')
          .map((revision) => {
            let date: string | undefined = '';

            if (revision.neverExecuted) {
              date = revision.nextDate !== '' ? revision.nextDate! : undefined;
            } else {
              date = revision.date !== '' ? revision.nextDate! : undefined;
            }

            return {
              revisionPlanId: revision.revisionPlanId ?? undefined,
              description: revision.description,
              date,
              revisionPeriod: Number(revision.revisionPeriod),
              deleted: revision.deleted,
              neverExecuted: revision.neverExecuted,
              deviceTypeId: revision.deviceTypeId ? Number(revision.deviceTypeId) : undefined,
            };
          });

        const mappedFaults = faults
          .filter(({ faultName }) => faultName !== '')
          .map(({ faultId, faultName, faultSeverityId, description, deleted }) => ({
            faultId: faultId ?? undefined,
            faultName,
            faultSeverityId: faultSeverityId ? Number(faultSeverityId) : undefined,
            description: description ?? '',
            deleted,
          }));

        return deviceUpdateAction({
          parameters: {
            deviceId: deviceId!,
          },
          payload: {
            deviceName,
            manufacturer: manufacturer === '' ? undefined : manufacturer,
            serialNumber: serialNumber === '' ? undefined : serialNumber,
            ...qrPayload,
            manufactured: String(manufactured) === '' ? undefined : manufactured,
            internalPossessionsNumber,
            internalNote,
            building,
            floor,
            room,
            longitude,
            latitude,
            deviceTypeId: Number(deviceTypeId),
            deviceSubtypeId: Number(deviceSubtypeId),
            departmentId: Number(departmentId),
            responsiblePersonId: responsiblePersonId ? Number(responsiblePersonId) : undefined,
            responsiblePersonNote,
            externalId,
            nextRevision: mappedRevisions,
            files: mappedFiles,
            faults: mappedFaults,
            structure,
            parentDeviceId: parentDeviceId ? Number(parentDeviceId) : undefined,
            childDeviceIds: childDeviceIds ? childDeviceIds.map((id) => Number(id)) : undefined,
            evidenceFields: Object.values(evidenceFields).map(({ fieldNumberValue, ...evidenceField }) => ({
              fieldNumberValue: typeof fieldNumberValue === 'number' ? fieldNumberValue : undefined,
              ...evidenceField,
            })),
          },
        });
      })
      .then(() => {
        notifications.show({
          title: 'Zariadenie bol úspešne upravený',
          message: `Zariadenie ${deviceName} bol úspešne upravený.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        if (deviceFormReferrer) {
          navigate(deviceFormReferrer.returnTo);
        } else if (DEVICE_REDIRECT_AFTER_SAVE === 'list') {
          navigate(DEVICES_PAGE_PATH.original);
        } else {
          forceRefresh();
        }
      })
      .catch(panic);
  }

  /**
   * Retrieves all files as a flat array with type set.
   */
  function retrieveDeviceFiles(files: DeviceGetResponse['files']) {
    return (files ?? []).map((file) => ({
      deviceFileId: file.deviceFileId,
      fileName: file.fileName,
      fileId: file.fileId,
      key: randomId(),
      uploadedAt: new Date(file.created!),
      originalFileName: '',
      deleted: false as const,
      isMainImage: file.isMainImage,
      hasKepSignature: file.hasKepSignature,
      type: file.fileType as DeviceFileType,
    }));
  }

  return (
    <DashboardLayout
      title={`${canEdit ? 'Úprava zariadenia: ' : 'Zariadenie: '}${device?.deviceName ?? ''}`}
      titleColor={`${loading ? '' : device?.status ? '' : 'red.9'}`}
      isDiscarded={!loading && !device?.status}
      breadcrumbs={[
        { title: 'Zariadenia', link: DEVICES_PAGE_PATH.original },
        { title: device?.deviceName ?? 'Načítavanie ...' },
      ]}
    >
      {device && (
        <DeviceForm
          key={`DeviceForm-${device.updated}`}
          initialDepartment={device.department}
          initialValues={{
            deviceName: device.deviceName,
            manufacturer: device.manufacturer ?? '',
            serialNumber: device.serialNumber ?? '',
            qrId: device.qrId ?? '',
            manufactured: device.manufactured ?? undefined,
            internalPossessionsNumber: device.internalPossessionsNumber ?? '',
            internalNote: device.internalNote ?? '',
            building: device.building ?? '',
            floor: device.floor ?? '',
            room: device.room ?? '',
            longitude: device.longitude ? parseFloat(device.longitude) : undefined,
            latitude: device.latitude ? parseFloat(device.latitude) : undefined,
            deviceTypeId: String(device.deviceType.deviceTypeId),
            deviceSubtypeId: String(device.deviceSubtype.deviceTypeId ?? 0),
            departmentId: String(device.department.departmentId),
            responsiblePersonId: device.responsiblePerson ? String(device.responsiblePerson.userId) : '',
            responsiblePersonNote: device.responsiblePersonNote ?? '',
            externalId: device.externalId ?? '',
            files: retrieveDeviceFiles(device.files),
            nextRevision: device.nextRevision
              ? device.nextRevision.map(
                  ({ revisionPlanId, description, date, revisionPeriod, externalId, neverExecuted, deviceTypeId }) => {
                    let parsedDate = '';
                    let parsedNextDate = '';

                    if (date) {
                      try {
                        parsedDate = stringifyDate(addMonths(new Date(date), -revisionPeriod));
                        parsedNextDate = stringifyDate(new Date(date));
                      } catch (error: any) {
                        panic(new Error(`Invalid date: ${date}`));
                      }
                    }

                    return {
                      revisionPlanId,
                      description,
                      date: neverExecuted ? '' : parsedDate,
                      nextDate: parsedNextDate,
                      revisionPeriod: String(revisionPeriod),
                      key: randomId(),
                      externalId,
                      deleted: false,
                      neverExecuted,
                      deviceTypeId: deviceTypeId ? String(deviceTypeId) : '',
                    };
                  }
                )
              : [
                  {
                    description: '',
                    date: '',
                    revisionPeriod: '',
                    key: randomId(),
                    deleted: false,
                    neverExecuted: false,
                  },
                ],
            faults: device.faults
              ? device.faults.map(({ faultId, faultName, faultSeverityId, description, fixed }) => ({
                  faultId,
                  faultName,
                  faultSeverityId: faultSeverityId ? String(faultSeverityId) : undefined,
                  description,
                  fixed,
                  key: randomId(),
                  deleted: false,
                }))
              : [],
            evidenceFields: device.evidenceFields.reduce((acc, field) => {
              // the API returns string for decimal numbers
              if (field.fieldType === 'number') {
                field.fieldNumberValue =
                  field.fieldNumberValue === undefined ? undefined : Number(field.fieldNumberValue);
              }

              acc[field.evidenceFieldId!] = field;

              return acc;
            }, {} as { [evidenceFieldId: number]: DeviceEvidenceFieldType }),
            structure: device.structure ?? 'simple',
            parentDeviceId: device.parentDevice ? String(device.parentDevice.deviceId) : '',
            childDeviceIds: device.childDevices?.map((child) => String(child.deviceId)),
          }}
          onSubmit={onSubmit}
          remoteSource={device.remoteSource}
          departmentOrganizationId={device.organization.organizationId}
          confirmDepartmentChange
          readOnly={{
            departmentId: !canEdit || !FEATURE_TOGGLE_MOVE_DEVICE_BETWEEN_DEPARTMENTS,
            // readonly so that the first time the edit page is rendered, the deviceSubType is not changed
            deviceSubtypeId: true,
            responsiblePersonId: true,
            deviceName: !canEdit,
            manufacturer: !canEdit,
            serialNumber: !canEdit,
            qrId: !canEdit,
            manufactured: !canEdit,
            internalPossessionsNumber: !canEdit,
            internalNote: !canEdit,
            building: !canEdit,
            floor: !canEdit,
            room: !canEdit,
            longitude: !canEdit,
            latitude: !canEdit,
            deviceTypeId: !canEdit,
            responsiblePersonNote: !canEdit,
            externalId: !canEdit || !device.remoteSource,
            nextRevision: !canEdit,
            faults: !canEdit,
            files: !canEdit,
            structure: !canEdit,
            parentDeviceId: !canEdit,
            childDeviceIds: !canEdit,
          }}
          hideFooter={!canEdit}
        />
      )}
    </DashboardLayout>
  );
}
